"use client";

import { Box, Button, Container, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useMemo } from "react";
import SalifyLink from "src/lib/components/link/salify-link";
import { toAssetImages } from "src/lib/utils";
import {
  LayoutViewMode,
  ResponsivePropsConfig,
  resolveResponsiveProps,
  splitDesktopAndResponsive,
} from "src/lib/utils/responsive-props";

export type AboutUsContentPosition = "left" | "center" | "right";

export type AboutUsStatItem = {
  value: string;
  label: string;
};

export type AboutUsButton = {
  text: string;
  link: string;
};

type AboutUsViewportData = {
  backgroundImage?: string;
  featuredImage?: string;
  title?: string;
  heading1?: string;
  heading2?: string;
  description?: string;
  stats?: AboutUsStatItem[];
  button?: AboutUsButton;
  contentPosition?: AboutUsContentPosition;
};

export interface VariantProps extends AboutUsViewportData {
  responsive?: ResponsivePropsConfig<AboutUsViewportData>;
}

const ACCENT = "#FFC107";

const useLayoutViewMode = (): LayoutViewMode => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));
  const isTablet = useMediaQuery(theme.breakpoints.between("tablet", "laptop"));
  if (isMobile) return "mobile";
  if (isTablet) return "tablet";
  return "desktop";
};

const textAlignFor = (position: AboutUsContentPosition) => {
  if (position === "center") return "center";
  if (position === "right") return "right";
  return "left";
};

const AboutUsDefaultV2 = (props: VariantProps) => {
  const viewMode = useLayoutViewMode();
  const { desktop, responsive } = splitDesktopAndResponsive(
    props as unknown as Record<string, unknown>,
  );

  const data = useMemo((): AboutUsViewportData => {
    const resolved = resolveResponsiveProps(
      {
        backgroundImage: (desktop.backgroundImage as string) || "",
        featuredImage: (desktop.featuredImage as string) || "",
        title: (desktop.title as string) || "",
        heading1: (desktop.heading1 as string) || "",
        heading2: (desktop.heading2 as string) || "",
        description: (desktop.description as string) || "",
        stats: (desktop.stats as AboutUsStatItem[]) || [],
        button: desktop.button as AboutUsButton | undefined,
        contentPosition: (desktop.contentPosition as AboutUsContentPosition) || "right",
      },
      responsive,
      viewMode,
      [
        "backgroundImage",
        "featuredImage",
        "title",
        "heading1",
        "heading2",
        "description",
        "stats",
        "button",
        "contentPosition",
      ],
    );

    return {
      backgroundImage: String(resolved.backgroundImage || ""),
      featuredImage: String(resolved.featuredImage || ""),
      title: String(resolved.title || ""),
      heading1: String(resolved.heading1 || ""),
      heading2: String(resolved.heading2 || ""),
      description: String(resolved.description || ""),
      stats: Array.isArray(resolved.stats) ? resolved.stats : [],
      button: resolved.button as AboutUsButton | undefined,
      contentPosition: (resolved.contentPosition as AboutUsContentPosition) || "right",
    };
  }, [desktop, responsive, viewMode]);

  const contentPosition = data.contentPosition || "right";
  const textAlign = textAlignFor(contentPosition);
  const stats = (data.stats || []).filter((item) => item?.value?.trim() || item?.label?.trim());
  const backgroundUrl = data.backgroundImage?.trim()
    ? toAssetImages(data.backgroundImage as string)
    : "";
  const featuredUrl = data.featuredImage?.trim() ? toAssetImages(data.featuredImage as string) : "";
  const isCentered = contentPosition === "center";
  const imageFirst = contentPosition === "right";

  const contentBlock = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        textAlign,
        alignItems:
          contentPosition === "center"
            ? "center"
            : contentPosition === "right"
              ? "flex-end"
              : "flex-start",
      }}
    >
      {data.title ? (
        <Typography
          component="p"
          sx={{
            color: "text.secondary",
            fontSize: { xs: 14, laptop: 15 },
            fontWeight: 500,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {data.title}
        </Typography>
      ) : null}

      {data.heading1 ? (
        <Typography
          component="h2"
          sx={{
            fontWeight: 700,
            fontSize: { xs: 26, tablet: 30, laptop: 36 },
            lineHeight: 1.2,
            color: "text.primary",
            maxWidth: isCentered ? 720 : "100%",
          }}
        >
          {data.heading1}
        </Typography>
      ) : null}

      {data.heading2 ? (
        <Typography
          component="h3"
          sx={{
            fontWeight: 600,
            fontSize: { xs: 18, laptop: 22 },
            lineHeight: 1.35,
            color: "text.primary",
            maxWidth: isCentered ? 720 : "100%",
          }}
        >
          {data.heading2}
        </Typography>
      ) : null}

      {data.description ? (
        <Typography
          component="div"
          sx={{
            fontSize: { xs: 14, laptop: 15 },
            lineHeight: 1.75,
            color: "text.secondary",
            whiteSpace: "pre-line",
            maxWidth: isCentered ? 720 : "100%",
          }}
        >
          {data.description}
        </Typography>
      ) : null}

      {stats.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              laptop: `repeat(${Math.min(stats.length, 4)}, minmax(0, 1fr))`,
            },
            gap: { xs: 2, laptop: 3 },
            width: "100%",
            maxWidth: isCentered ? 720 : "100%",
            mt: 0.5,
          }}
        >
          {stats.map((stat, index) => (
            <Box key={`${stat.value}-${index}`}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 28, laptop: 34 },
                  lineHeight: 1.1,
                  color: ACCENT,
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: { xs: 13, laptop: 14 },
                  color: "text.secondary",
                  lineHeight: 1.4,
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      {data.button?.text ? (
        <Box
          sx={{ mt: 1, width: isCentered ? "100%" : "auto", maxWidth: isCentered ? 360 : "none" }}
        >
          <SalifyLink href={data.button.link || "#"}>
            <Button
              variant="contained"
              fullWidth={isCentered}
              sx={{
                bgcolor: ACCENT,
                color: "#111",
                fontWeight: 600,
                px: 4,
                py: 1.35,
                borderRadius: 1,
                boxShadow: "none",
                minWidth: isCentered ? undefined : 200,
                "&:hover": { bgcolor: "#e6ad06", boxShadow: "none" },
              }}
            >
              {data.button.text}
            </Button>
          </SalifyLink>
        </Box>
      ) : null}
    </Box>
  );

  const imageBlock = featuredUrl ? (
    <Box
      sx={{
        width: "100%",
        borderRadius: { xs: 1, laptop: 1.5 },
        overflow: "hidden",
        minHeight: { xs: 220, laptop: 360 },
        bgcolor: "grey.100",
      }}
    >
      <Box
        component="img"
        src={featuredUrl}
        alt={data.heading1 || data.title || "About us"}
        loading="lazy"
        sx={{
          width: "100%",
          height: "100%",
          minHeight: { xs: 220, laptop: 360 },
          objectFit: "cover",
          display: "block",
        }}
      />
    </Box>
  ) : null;

  return (
    <Box
      component="section"
      id="about-us"
      sx={{
        position: "relative",
        py: { xs: 5, laptop: 8 },
        bgcolor: "background.paper",
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {backgroundUrl ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.92)",
          }}
        />
      ) : null}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {isCentered ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
            {imageBlock}
            {contentBlock}
          </Box>
        ) : (
          <Grid container spacing={{ xs: 3, laptop: 5 }} alignItems="center">
            {imageFirst && imageBlock ? (
              <Grid item xs={12} laptop={6}>
                {imageBlock}
              </Grid>
            ) : null}
            <Grid item xs={12} laptop={imageBlock ? 6 : 12}>
              {contentBlock}
            </Grid>
            {!imageFirst && imageBlock ? (
              <Grid item xs={12} laptop={6}>
                {imageBlock}
              </Grid>
            ) : null}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default AboutUsDefaultV2;
